import React, {useCallback, useState} from "react";
import {observer} from "mobx-react";
import {CyClass} from "../../../../../common/cy-classes";
import {IModalDialogHandler} from "../../../../services/dialog/dialog.service.interface";
import {Check} from "@sparkware/app-utils";
import {ModalDialogActionsComponent} from "../../../modal-dialog/modal-dialog-actions.component";
import {useServices} from "../../../../react-hooks/use-services.hook";
import {DialogTextBox} from "../../../modal-dialog/boxes/text/dialog-text.box";
import {RadioInputBox} from "../../../../basic-ui-components/form/radioinput.box";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {FlexContainerBox} from "../../../../themes/architecture/css-helpers/flex";
import {RadioInputLabelBox} from "./boxes/radio-input.label.box";
import classNames from "classnames";
import {CurrencyAmountInput} from "../../../../basic-ui-components/form/currency-input/currency-input.component";
import {AmountSpan} from "./boxes/amount-span.box";

enum LimitsRadioButtons {
    DefaultLimit,
    CustomLimit
}

interface AddFundsDialogComponentProps {
    dialogHandler: IModalDialogHandler;
    onAddFunds: (amount: number) => void;
    minLimitInCents: () => number;
    maxLimitInCents: () => number;
    minBet?: number;
    analytics?: {
        onAddFunds?: (amountInCents: number, allBalance: boolean) => void;
        onCashierClick?: () => void;
    };
}

export const AddFundsDialogComponent: React.FC<AddFundsDialogComponentProps> = observer((props) => {
    const services = useServices();
    const [selectedOption, setSelectedOption] = useState<LimitsRadioButtons>(LimitsRadioButtons.DefaultLimit);
    const [customAmountTextInputValue, setCustomAmountTextInputValue] = useState<string>("");
    const [amountInCents, setAmountInCents] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const _minLimitCents = props.minLimitInCents();
    const _maxLimitCents = props.maxLimitInCents();

    // When calling validate right after setAmount in onCustomAmountChange I get stalled values. So I send values as parameters.
    // All other values that I don;t expect to change are left in dependecies array
    const validate = useCallback((_amountInCents, _selectedOption) => {
        let errMsg: string | null = null;
        if (_amountInCents > _maxLimitCents) {
            errMsg = services.translation.translate('GAME_LIMIT_INSUFFICIENT_FUNDS');
        } else if (_selectedOption !== LimitsRadioButtons.DefaultLimit) {
            if (_amountInCents <= 0 ) {
                errMsg = services.translation.translate('GAME_LIMIT_VALUE_ERROR');
            }
        }
        setErrorMessage(errMsg);
            return Check.isNullOrUndefined(errMsg);
    }, [_maxLimitCents, services.translation]);

    const onCashierClick = useCallback( () => {
        if (props.analytics && props.analytics.onCashierClick) {
            props.analytics.onCashierClick();
        }
        services.cashier.openDeposit({
            channel: {
                source: CHANNEL_SOURCE.casinoClient,
                area: AreaType.Header,
                element: CHANNEL_ELEMENTS.cashierAction
            }
        })
    }, [services.cashier, props.analytics]);

    const onAddFundsClick = () => {

        if (!validate(amountInCents, selectedOption)) {
            return;
        }

        let amountToAdd = amountInCents;
        if(LimitsRadioButtons.DefaultLimit === selectedOption) {
            amountToAdd = _maxLimitCents;
        }

        props.dialogHandler.close();
        if (props.analytics?.onAddFunds) {
            props.analytics.onAddFunds(amountToAdd, LimitsRadioButtons.DefaultLimit === selectedOption);
        }
        props.onAddFunds(amountToAdd);
    }

    const onCustomAmountChange = useCallback((targetValue: string) => {
        let value = targetValue === "" ? 0 : parseFloat(targetValue);
        if(Check.isNumber(value) && /^\d+(\.\d{1,2})?$/.test(targetValue)) {
            // I am doing math.round after multiplying with 100 because in javascript 10.2 * 100 = 1019.(9)
            value = Math.round(value * 100);
            setAmountInCents(value);
            validate(value, LimitsRadioButtons.CustomLimit);
        } else {
            // set amount 0 because if accidentally presses top up
            setAmountInCents(0);
            setErrorMessage(services.translation.translate('GAME_LIMIT_VALUE_ERROR'));
        }
        setSelectedOption(LimitsRadioButtons.CustomLimit);
        setCustomAmountTextInputValue(targetValue);
    }, [validate, services.translation]);

    const onTotalLimitSelected = useCallback(() => {
        const availableBalance = _maxLimitCents;
        setAmountInCents(availableBalance)
        setSelectedOption(LimitsRadioButtons.DefaultLimit);
        setCustomAmountTextInputValue("");
        setErrorMessage(null);
    }, [_maxLimitCents]);

    return <div className={classNames('dialog-form', CyClass('cy-modal-content'))}>
        <DialogTextBox>{services.translation.translate('REGULATORY_LIMIT_LABEL')} <AmountSpan>{services.currency.formatMoney(_maxLimitCents, true)}</AmountSpan></DialogTextBox>
        <DialogTextBox>
            <FlexContainerBox alignItems="center">
                <RadioInputBox
                    name="gameLimit"
                    id="gameLimit_totalBalance"
                    value={LimitsRadioButtons.DefaultLimit}
                    checked={selectedOption === LimitsRadioButtons.DefaultLimit}
                    onChange={onTotalLimitSelected}
                />
                <RadioInputLabelBox as="label" htmlFor="gameLimit_totalBalance">
                    {services.translation.translate("ADD_FUNDS_TOTAL_BALANCE")}
                </RadioInputLabelBox>
            </FlexContainerBox>
        </DialogTextBox>
        { _maxLimitCents > _minLimitCents ?
        <DialogTextBox>
            <div>{services.translation.translate("ADD_FUNDS_OTHER_AMOUNT")}</div>
            <div>
                <CurrencyAmountInput
                    value={customAmountTextInputValue}
                    currencySymbol={services.currency.getCurrencySymbol}
                    onChange={onCustomAmountChange}
                    cyClass={CyClass('cy-add-funds-amount')}
                    errorMessage={errorMessage}
                />
            </div>
        </DialogTextBox>
        : null }
        <ModalDialogActionsComponent
            primaryAction={{
                text: services.translation.translate("ADD_FUNDS_TOP_UP"),
                onClick: onAddFundsClick,
                className: CyClass('cy-add-funds-topup-button')
            }}
            secondaryAction={{
                text: services.translation.translate("CASHIER"),
                onClick: onCashierClick,
                className: CyClass('cy-add-funds-cashier-button')
            }}
            margin="3rem auto 0"
        />
    </div>;
})